/**
 * Zielgefäß für Vorlagen, in denen etwas hinzukommt: Rührschüssel auf der
 * Arbeitsfläche, Topf oder Pfanne auf dem Herd. Alle stehen so, dass oben
 * rechts Platz für ein Gießgefäß oder eine Hand bleibt.
 */
import { compact } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { counter } from './stage.ts';
import { GRATE_Y, stove, stoveKnob } from './stove.ts';
import { bowl, pan, pot } from './vessels.ts';
import type { Vessel } from './vessels.ts';

/** Zielgefäße für Parameter. */
export const TARGET_VESSELS = {
  bowl: 'Schüssel',
  pot: 'Topf auf dem Herd',
  pan: 'Pfanne auf dem Herd',
} as const;

/** Kennung eines Zielgefäßes. */
export type TargetVessel = keyof typeof TARGET_VESSELS;

const BOWL = { cx: 100, rim: 84, width: 132, height: 50 } as const;
const POT = { cx: 100, width: 120, height: 46 } as const;
const PAN = { cx: 96, width: 112 } as const;

/** Flammenstärke unter Topf und Pfanne. */
const POT_POWER = 0.35;

/** Liest das Zielgefäß aus dem Schritttext. */
export function targetFromText(text: string): { vessel?: TargetVessel } {
  const lower = text.toLowerCase();
  if (/pfanne|braten|brät/.test(lower)) return { vessel: 'pan' };
  if (/topf|herd|köcheln|kochen|brühe|suppe|sauce|soße/.test(lower)) return { vessel: 'pot' };
  if (/schüssel|teig|rührschüssel/.test(lower)) return { vessel: 'bowl' };
  return {};
}

/** Gefäß zur Kennung. */
export function targetVessel(kind: TargetVessel): Vessel {
  if (kind === 'pan') {
    return pan(PAN.cx, GRATE_Y - 13 - PAN.width * 0.1, PAN.width);
  }
  return kind === 'pot'
    ? pot(POT.cx, GRATE_Y - POT.height, POT.width, POT.height)
    : bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
}

/**
 * Setzt die Szene zusammen: Arbeitsfläche, gegebenenfalls Herd, Rückwand,
 * Inhalt, Vorderwand und alles, was davor liegt.
 */
export function targetScene(
  kind: TargetVessel,
  vessel: Vessel,
  t: number,
  inside: readonly MaybeNode[],
  above: readonly MaybeNode[],
): SceneNode[] {
  const onStove = kind !== 'bowl';
  const burner = onStove ? stove(vessel.cx, POT_POWER, t) : null;
  return compact([
    ...counter(),
    ...(burner?.back ?? []),
    ...vessel.back,
    ...inside,
    ...vessel.front,
    ...(burner?.front ?? []),
    onStove ? stoveKnob(214, POT_POWER) : null,
    ...above,
  ]);
}
